import React, { ChangeEvent, useEffect, useState, useMemo, useCallback } from 'react';
import styles from './ImageUploadForm.module.scss';
import Button from 'src/components/button/Button';
import IconImage from '../../../../../../assets/icons/content_type_image.svg?react';
import IconTrash from '../../../../../../assets/icons/trash.svg?react';
import IconReplace from '../../../../../../assets/icons/refresh_ccw.svg?react';
import { getMediaAbsolutePath } from '../../../../../../services/File';
import { toBase64 } from '@utils/helpers';
import { logger } from '../../../../../../utils/logger';

type Props = {
  url: string | File | undefined;
  onChange: (url: string, file: File | undefined) => void;
  hasError?: boolean;
  subTitle?: string;
};

function ImageUploadForm(props: Props) {
  const { url, onChange, hasError, subTitle } = props;

  const [file, setFile] = useState<File | undefined>(undefined);
  const [fileBase64, setFileBase64] = useState<string | undefined>(undefined);

  const handleClear = useCallback(() => {
    onChange('', undefined);
    setFileBase64(undefined);
  }, [onChange]);

  const setFileObjectAndReturnBase64 = useCallback(async (file: File): Promise<string> => {
    setFile(file);
    const base64Str = await toBase64(file);
    setFileBase64(base64Str);
    return base64Str;
  }, []);

  const handleChange = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target?.files?.[0];
    if (file) {
      try {
        const base64Str = await setFileObjectAndReturnBase64(file);
        onChange(base64Str, file);
      } catch (error) {
        logger.error('Error processing file:', error);
        handleClear();
        // Propagate the error to parent components
        throw error;
      }
    } else {
      handleClear();
    }
  }, [setFileObjectAndReturnBase64, onChange, handleClear]);

  useEffect(() => {
    if (url && typeof url === 'object') {
      setFileObjectAndReturnBase64(url);
    }
  }, [setFileObjectAndReturnBase64, url]);

  useEffect(() => {
    return () => {
      setFileBase64(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const name = useMemo(() => file?.name || ((typeof url === 'string' && url?.split('/').pop()) ?? 'No name'), [file?.name, url]);
  const fileUrl = useMemo(() => typeof url === 'string' ? url : undefined, [url]);
  const hasContent = useMemo(() => !!url, [url]);

  return (
    <div className={`${styles.wrapper} error`}>
      <div className={styles.content}>
        {hasContent || !!fileBase64 ? <div className={styles.withContent}>
          <div className={styles.imageWrapper}>
            <div className={styles.image} style={{ backgroundImage: !!fileBase64 ? `url("${fileBase64}")` : `url(${getMediaAbsolutePath(fileUrl)})`}}/>
          </div>
          <div className={styles.imageName}>{name}</div>
          <div className={styles.buttons}>
            <div className={styles.replaceButtonContainer}>
              <input className={styles.replace} type={'file'} accept={'image/*'} onChange={handleChange}/>
              <Button className={styles.buttonReplace} size={'small'} label={'Replace'} leftIcon={<IconReplace className={styles.iconReplace}/>} outline={true}/>
            </div>
            <Button className={styles.buttonDelete} onClick={handleClear} size={'small'} label={'Delete'} leftIcon={<IconTrash className={styles.iconTrash}/>} outline={true}/>
          </div>  
        </div> : <div className={styles.uploader}>
          <input className={styles.input} type={'file'} accept={'image/*'} onChange={handleChange}/>
          <Button label={'Upload image'} color={'orange'} outline={true} className={styles.inputButton}
                  leftIcon={<IconImage/>}/>
          <div className={styles.uploaderText}>{'or drag & drop image file here'}</div>        
        </div>}
        {hasError ?<div className={styles.subtitle}>{subTitle}</div> : null}
      </div>
    </div>
  );
}

export default ImageUploadForm;
